import React, { useState, useMemo, useEffect, useCallback } from 'react'
import logger from '@adc/logger'
import {
  Modal,
  Icon,
  Button,
  CountdownButton,
  Text,
  SelectField,
  MarkdownRenderer,
  Alert,
  useApplicationNotifications,
  NOTIFICATION_ROLES,
  coreConstants,
  coreUtils,
} from '@adc/ui-morpheus'
import { InputField, TextAreaField, CheckboxField } from '@adc/ui-core'
import configment from '@adc/configment'
import classnames from 'classnames/bind'
import type {
  CognitionEvaluationTaskName,
  CognitionPrompt,
} from '@adc/sdk/types'
import * as graph from '@adc/sdk'
import {
  fetchPrompts,
  fetchDefaultPrompt,
  upsertPrompt,
  deletePrompts,
} from '../../../providers/aiEvaluations'
import styles from './styles.scss'

const cx = classnames.bind(styles)

export interface OverridePromptModalProps {
  isOpen: boolean
  onClose: () => void
  task: CognitionEvaluationTaskName
}

type ModalMode = 'view' | 'edit' | 'create'

const OverridePromptModal: React.FC<OverridePromptModalProps> = ({
  isOpen,
  onClose,
}) => {
  const notifications = useApplicationNotifications()
  const [mode, setMode] = useState<ModalMode>('view')
  const [selectedMethod, setSelectedMethod] = useState<string>('')
  const [selectedPromptUrn, setSelectedPromptUrn] = useState<string>('')
  const [prompts, setPrompts] = useState<CognitionPrompt[]>([])
  const [defaultPrompt, setDefaultPrompt] = useState<CognitionPrompt | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Edit/create form fields
  const [editableDescription, setEditableDescription] = useState<string>('')
  const [editableText, setEditableText] = useState<string>('')
  const [editableActive, setEditableActive] = useState<boolean>(false)
  const [confirmText, setConfirmText] = useState<string>('')

  const isOverrideConfirmationEnabled = configment.get(
    'PROMPT_OVERRIDE_CONFIRMATION_ENABLED',
    false
  )
  const currentEnvName = (() => {
    const env = configment.get('ENV', '')
    if (!env) return ''
    return env === 'prod' ? 'production' : env
  })()

  const promptOverrideWarningMessage = currentEnvName
    ? `Type "confirm" to override in ${currentEnvName}.`
    : 'Type "confirm" to override the system prompt.'

  const isConfirmed = confirmText.trim().toLowerCase() === 'confirm'

  const methodOptions = useMemo(
    () =>
      Object.values(graph.enums.CognitionEvaluationMethod)
        .map(value => ({
          text: coreUtils.formatters.formatEnum(value),
          value,
        }))
        .sort((a, b) => a.text.localeCompare(b.text)),
    []
  )

  const promptOptions = useMemo(
    () =>
      prompts
        .map(prompt => ({
          text:
            prompt.status === graph.enums.CognitionPromptStatus.Stable
              ? `${prompt.description} (ACTIVE)`
              : prompt.description,
          value: prompt.urn,
        }))
        .sort((a, b) => a.text.localeCompare(b.text)),
    [prompts]
  )

  const selectedPrompt = useMemo(
    () => prompts.find(p => p.urn === selectedPromptUrn) ?? null,
    [prompts, selectedPromptUrn]
  )

  const loadPrompts = useCallback((method: string) => {
    if (!method) {
      setPrompts([])
      setDefaultPrompt(null)
      return
    }

    setIsLoading(true)

    Promise.all([
      fetchPrompts({
        evaluation_method_codes: [
          method as graph.enums.CognitionEvaluationMethod,
        ],
      }),
      fetchDefaultPrompt(method as graph.enums.CognitionEvaluationMethod).catch(
        error => {
          logger.error('Failed to fetch default prompt:', error)
          return null
        }
      ),
    ])
      .then(([promptsData, defaultPromptData]) => {
        setPrompts(promptsData)
        setDefaultPrompt(defaultPromptData)
        const stable = promptsData.find(
          p => p.status === graph.enums.CognitionPromptStatus.Stable
        )
        setSelectedPromptUrn(stable?.urn ?? promptsData[0]?.urn ?? '')
        return null
      })
      .catch(error => {
        logger.error('Failed to fetch prompts:', error)
        setPrompts([])
        setDefaultPrompt(null)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  useEffect(() => {
    loadPrompts(selectedMethod)
  }, [selectedMethod, loadPrompts])

  useEffect(() => {
    if (!isOpen) {
      setMode('view')
      setSelectedMethod('')
      setSelectedPromptUrn('')
      setPrompts([])
      setDefaultPrompt(null)
      setIsLoading(false)
      setIsSaving(false)
      setEditableDescription('')
      setEditableText('')
      setEditableActive(false)
      setConfirmText('')
    }
  }, [isOpen])

  const handleMethodChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    meta?: { value: string }
  ) => {
    const value = meta?.value || event?.target?.value || ''
    setSelectedMethod(value)
    setSelectedPromptUrn('')
  }

  const handlePromptChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    meta?: { value: string }
  ) => {
    const value = meta?.value || event?.target?.value || ''
    setSelectedPromptUrn(value)
  }

  const handleClose = () => {
    onClose()
  }

  const handleCreateClick = () => {
    setEditableDescription('')
    setEditableText('')
    setEditableActive(false)
    setMode('create')
  }

  const handleEditClick = () => {
    if (!selectedPrompt) return
    setEditableDescription(selectedPrompt.description)
    setEditableText(selectedPrompt.text)
    setEditableActive(
      selectedPrompt.status === graph.enums.CognitionPromptStatus.Stable
    )
    setMode('edit')
  }

  const handleCancel = () => {
    setMode('view')
    setEditableDescription('')
    setEditableText('')
    setEditableActive(false)
    setConfirmText('')
  }

  const handleSave = () => {
    if (
      !selectedMethod ||
      !editableDescription.trim() ||
      !editableText.trim()
    ) {
      return
    }

    setIsSaving(true)

    const input = {
      evaluation_method_code:
        selectedMethod as graph.enums.CognitionEvaluationMethod,
      description: editableDescription.trim(),
      text: editableText.trim(),
      status: editableActive
        ? graph.enums.CognitionPromptStatus.Stable
        : graph.enums.CognitionPromptStatus.Draft,
      ...(mode === 'edit' && selectedPromptUrn
        ? { urn: selectedPromptUrn }
        : {}),
    }

    upsertPrompt(input)
      .then(savedPrompt => {
        notifications.toast.open({
          title:
            mode === 'create'
              ? 'Override prompt created successfully'
              : 'Override prompt updated successfully',
        })
        setMode('view')
        setEditableDescription('')
        setEditableText('')
        setEditableActive(false)
        setSelectedPromptUrn(savedPrompt.urn)
        return loadPrompts(selectedMethod)
      })
      .catch(error => {
        logger.error(`Failed to ${mode} prompt:`, error)
        notifications.toast.open({
          title:
            mode === 'create'
              ? 'Failed to create override prompt'
              : 'Failed to update override prompt',
          role: NOTIFICATION_ROLES.ERROR,
        })
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const descriptionError = useMemo(() => {
    const trimmed = editableDescription.trim()
    if (!trimmed) return undefined
    const duplicate = prompts.find(
      p =>
        p.description.toLowerCase() === trimmed.toLowerCase() &&
        (mode === 'create' || p.urn !== selectedPromptUrn)
    )
    return duplicate
      ? 'An override prompt with this name already exists'
      : undefined
  }, [editableDescription, prompts, mode, selectedPromptUrn])

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditableDescription(e.target.value)
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditableText(e.target.value)
  }

  const handleActiveChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    data?: { checked?: boolean }
  ) => {
    setEditableActive(data?.checked ?? !editableActive)
  }

  const handleDelete = () => {
    if (!selectedPromptUrn) return

    setIsSaving(true)
    deletePrompts([selectedPromptUrn])
      .then(() => {
        notifications.toast.open({
          title: 'Prompt deleted successfully',
        })
        setSelectedPromptUrn('')
        return loadPrompts(selectedMethod)
      })
      .catch(error => {
        logger.error('Failed to delete prompt:', error)
        notifications.toast.open({
          title: 'Failed to delete prompt',
          role: NOTIFICATION_ROLES.ERROR,
        })
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const handleFillWithSystemPrompt = () => {
    if (defaultPrompt) {
      setEditableText(defaultPrompt.text)
    }
  }

  const getTitle = (): string => {
    if (mode === 'create') return 'Create an Override Prompt'
    if (mode === 'edit') return 'Edit Override Prompt'
    return 'Override Prompt'
  }

  const title = getTitle()

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size='small'
      elmId='override_prompt_modal'
      closeOnDimmerClick={false}
      closeIcon={
        <Icon
          className='close'
          name='times'
          iconStyle='regular'
          fitted
          onClick={handleClose}
        />
      }
      className={cx('modal')}
    >
      <Modal.Header>
        <Text size='large' weight='heavy'>
          {title}
        </Text>
      </Modal.Header>

      <Modal.Content>
        <div className={cx('content-wrapper')}>
          {mode === 'view' ? (
            <>
              <div className={cx('method-selector')}>
                <Text size='small' color='gray' className={cx('label')}>
                  Method
                </Text>
                <SelectField
                  value={selectedMethod}
                  onChange={handleMethodChange}
                  options={methodOptions}
                  placeholder='Select a Method'
                  elmId='method_selector'
                  hideWrapper
                  search
                />
              </div>

              {isLoading && (
                <div className={cx('loading-state')}>
                  <Icon
                    size='huge'
                    name='spinner'
                    iconStyle='light'
                    color='grayMedium'
                    loading
                  />
                  <Text color='grayMedium'>Loading prompts...</Text>
                </div>
              )}

              {!isLoading && selectedMethod && prompts.length > 0 && (
                <div className={cx('prompt-selector')}>
                  <Text size='small' color='gray' className={cx('label')}>
                    Override Prompt (Name)
                  </Text>
                  <SelectField
                    value={selectedPromptUrn}
                    onChange={handlePromptChange}
                    options={promptOptions}
                    placeholder='Select an Override Prompt'
                    elmId='prompt_selector'
                    hideWrapper
                    search
                  />
                </div>
              )}

              {!isLoading && selectedPrompt && (
                <div className={cx('prompt-section')}>
                  <Text size='small' color='gray' className={cx('label')}>
                    Prompt
                  </Text>
                  <div className={cx('prompt-display')}>
                    <MarkdownRenderer content={selectedPrompt.text} />
                  </div>
                </div>
              )}

              {!isLoading &&
                selectedMethod &&
                prompts.length > 0 &&
                !selectedPromptUrn && (
                  <div className={cx('empty-state')}>
                    <Text color='gray'>
                      Select a prompt to view its content
                    </Text>
                  </div>
                )}

              {!isLoading && selectedMethod && prompts.length === 0 && (
                <div className={cx('empty-state')}>
                  <Text color='gray'>
                    No override prompts found for this method
                  </Text>
                </div>
              )}

              {!selectedMethod && !isLoading && (
                <div className={cx('empty-state')}>
                  <Text color='gray'>
                    Select a method to view its override prompts
                  </Text>
                </div>
              )}
            </>
          ) : (
            <>
              <div className={cx('read-only-field')}>
                <Text size='small' color='gray' className={cx('label')}>
                  Method
                </Text>
                <div className={cx('read-only-value')}>
                  <Text>{coreUtils.formatters.formatEnum(selectedMethod)}</Text>
                </div>
              </div>

              <div className={cx('name-row')}>
                <div className={cx('name-field')}>
                  <InputField
                    elmId='prompt_description_input'
                    label='Override Prompt (Name)'
                    value={editableDescription}
                    onChange={handleDescriptionChange}
                    placeholder='Enter name...'
                    fluid
                    error={descriptionError}
                    touched={!!descriptionError}
                  />
                </div>
                <div className={cx('active-toggle')}>
                  <CheckboxField
                    elmId='prompt_active_toggle'
                    checked={editableActive}
                    onChange={handleActiveChange}
                    label='Active'
                    toggle
                  />
                </div>
              </div>

              <div className={cx('form-field')}>
                <Text size='small' color='gray' className={cx('label')}>
                  Prompt
                </Text>
                <TextAreaField
                  label=''
                  elmId='prompt_text_textarea'
                  value={editableText}
                  onChange={handleTextChange}
                  placeholder='Enter prompt text...'
                  className={cx('prompt-textarea')}
                />
                {defaultPrompt && (
                  <button
                    type='button'
                    className={cx('fill-link')}
                    onClick={handleFillWithSystemPrompt}
                  >
                    Fill with system prompt
                  </button>
                )}
              </div>

              {isOverrideConfirmationEnabled && editableActive && (
                <Alert
                  type='inline'
                  role='caution'
                  showIcon
                  header='Override prompt?'
                  content={
                    <>
                      <Text size='small' className={cx('confirm-alert-text')}>
                        {promptOverrideWarningMessage}
                      </Text>
                      <InputField
                        elmId='prompt_confirm_input'
                        label=''
                        value={confirmText}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setConfirmText(e.target.value)
                        }
                        placeholder='confirm'
                        fluid
                      />
                    </>
                  }
                />
              )}
            </>
          )}
        </div>
      </Modal.Content>

      <Modal.Actions>
        <div className={cx('actions')}>
          {mode === 'view' ? (
            <>
              <CountdownButton
                role='secondary'
                onClick={handleDelete}
                elmId='override_delete_button'
                disabled={!selectedPromptUrn}
                confirmColor={coreConstants.ColorNames.Error}
                confirmLabel='Click to Confirm'
                loading={isSaving}
                color={coreConstants.ColorNames.Error}
              >
                Delete
              </CountdownButton>
              <div className={cx('actions-right')}>
                <Button
                  color={coreConstants.ColorNames.Gray}
                  onClick={handleClose}
                  elmId='override_close_button'
                >
                  Close
                </Button>
                <Button
                  onClick={handleCreateClick}
                  elmId='override_create_button'
                  disabled={!selectedMethod}
                >
                  Create
                </Button>
                <Button
                  onClick={handleEditClick}
                  role='primary'
                  elmId='override_edit_button'
                  disabled={!selectedPromptUrn}
                >
                  Edit
                </Button>
              </div>
            </>
          ) : (
            <div className={cx('actions-right')}>
              <Button onClick={handleCancel} elmId='override_cancel_button'>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                role='primary'
                elmId='override_save_button'
                disabled={
                  !selectedMethod ||
                  !editableDescription.trim() ||
                  !editableText.trim() ||
                  !!descriptionError ||
                  (isOverrideConfirmationEnabled &&
                    editableActive &&
                    !isConfirmed)
                }
                loading={isSaving}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </Modal.Actions>
    </Modal>
  )
}

export default OverridePromptModal
